import { createColumnHelper } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "semantic-ui-react";

import { ReactTable, useCan } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { CreateEditKeyLockerModal, DeleteKeyLockerModal, KeyLockerBoxesModal } from "modules/KeyLockersDashboard/components";
import { useDisableKeyLockerMutation, useEnableKeyLockerMutation, useKeyLockersQuery } from "modules/KeyLockersDashboard/components/KeyLockersTable/hooks";
import "modules/KeyLockersDashboard/components/KeyLockersTable/KeyLockersTable.scss";

import type { KeyLocker } from "models";

const columnHelper = createColumnHelper<KeyLocker>();

export const KeyLockersTable = () => {
  const { t } = useTranslation();

  const [deleteKeyLockerId, setDeleteKeyLockerId] = useState(0);
  const [keyLockerBoxDetailsId, setKeyLockerBoxDetailsId] = useState(0);
  const [editKeyLockerPayload, setEditKeyLockerPayload] = useState<KeyLocker | null>(null);
  const [enableDisableKeyLockerId, setEnableDisableKeyLockerId] = useState(0);

  const canUpdateKeyLocker = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.Keylockers);
  const canDeleteKeyLocker = useCan(ACL_ACTIVITIES.Delete, ACL_RESOURCES.Keylockers);
  const canListKeyLockers = useCan(ACL_ACTIVITIES.List, ACL_RESOURCES.Keylockers);

  const { data: keyLockers, isFetching: isKeyLockersFetching } = useKeyLockersQuery();
  const { mutate: enableKeyLocker, isPending: isEnableKeyLockerPending } = useEnableKeyLockerMutation();
  const { mutate: disableKeyLocker, isPending: isDisableKeyLockerPending } = useDisableKeyLockerMutation();

  useEffect(() => {
    if (!isEnableKeyLockerPending && !isDisableKeyLockerPending) setEnableDisableKeyLockerId(0);
  }, [isEnableKeyLockerPending, isDisableKeyLockerPending]);

  const columns = [
    columnHelper.accessor((row, idx) => ({ row, idx }), {
      id: "index",
      size: 20,
      header: () => <span className="row-index"> # </span>,
      cell: ({
        row: {
          index,
          original: { active }
        }
      }) => (
        <div className={`${active ? "active-key-locker" : "disabled-key-locker"} row-index-container`}>
          <span className={active ? "active-key-locker-text" : "disabled-key-locker-text"}>{index + 1}</span>
        </div>
      )
    }),
    columnHelper.accessor(row => row, {
      id: "name",
      size: 170,
      header: () => <span className="header-title">{t("v8_name", "Name")}</span>,
      cell: ({
        row: {
          original: { name }
        }
      }) => <span className="text-ellipsis">{name}</span>
    }),
    columnHelper.accessor(row => row, {
      id: "lockers",
      size: 200,
      header: () => <span className="header-title"> {t("v8_lockers_active_total", "Lockers (Active/Total)")} </span>,
      cell: ({
        row: {
          original: { active_box_count, box_count }
        }
      }) => <span className="text-ellipsis">{`${active_box_count}/${box_count}`}</span>
    }),
    columnHelper.accessor(row => row, {
      id: "keys",
      size: 150,
      header: () => <span className="header-title"> {t("v8_keys", "Keys")} </span>,
      cell: ({
        row: {
          original: { key_count }
        }
      }) => <span className="text-ellipsis">{key_count}</span>
    }),
    columnHelper.accessor(row => row, {
      id: "version",
      size: 130,
      header: () => <span className="header-title"> {t("v8_version", "Version")} </span>,
      cell: ({
        row: {
          original: { frontend_version }
        }
      }) => <span className="text-ellipsis">{frontend_version}</span>
    }),
    columnHelper.accessor(row => row, {
      id: "2fa",
      size: 100,
      header: () => (
        <div className="text-align-center">
          <span className="header-title"> {t("v8_2fa_to_drop_key", "2FA To Drop Key")} </span>
        </div>
      ),
      cell: ({
        row: {
          original: { is_2fa_to_drop_key_enabled }
        }
      }) => (
        <div className="drop-key-icon">
          <Icon className={is_2fa_to_drop_key_enabled ? "check" : "close"} color={is_2fa_to_drop_key_enabled ? "green" : "red"} />
        </div>
      )
    }),
    columnHelper.accessor(row => row, {
      id: "actions",
      size: 50,
      header: () => "",
      cell: ({ row: { original } }) => {
        const canEnableDisableKeyLocker = canUpdateKeyLocker && (!original.active || original.key_count <= 0);
        const canOpenKeyLockerBoxDetails = canListKeyLockers && original.box_count > 0;

        return (
          <div className="actions-container">
            <div
              className="action-button-container"
              onClick={() => {
                if (!canEnableDisableKeyLocker) return;

                setEnableDisableKeyLockerId(original.id);
                original.active ? disableKeyLocker(original.id) : enableKeyLocker(original.id);
              }}
            >
              <Icon
                className={`${original.active ? "circle pause" : "play"} action-button-icon-center`}
                color={original.active ? "red" : "green"}
                disabled={!canEnableDisableKeyLocker || (enableDisableKeyLockerId === original.id && (isDisableKeyLockerPending || isEnableKeyLockerPending))}
              />
            </div>
            <div className="action-button-container" onClick={() => canOpenKeyLockerBoxDetails && setKeyLockerBoxDetailsId(original.id)}>
              <Icon className="list action-button-icon-center" color="purple" disabled={!canOpenKeyLockerBoxDetails} />
            </div>
            <div className="action-edit-button-container" onClick={() => canUpdateKeyLocker && setEditKeyLockerPayload(original)}>
              <Icon className="icon pen to square edit-icon action-button-icon-center" color="#32bf53" disabled={!canUpdateKeyLocker} />
            </div>
            <div className="action-button-container" onClick={() => canDeleteKeyLocker && setDeleteKeyLockerId(original.id)}>
              <Icon className="trash action-button-icon-center" color="red" disabled={!canDeleteKeyLocker} />
            </div>
          </div>
        );
      }
    })
  ];

  return (
    <>
      <div className="KeyLockersTable">
        <ReactTable isFetching={isKeyLockersFetching} columns={columns} data={keyLockers} />
      </div>

      <DeleteKeyLockerModal keyLockerId={deleteKeyLockerId} resetDeleteKeyLockerId={() => setDeleteKeyLockerId(0)} />
      <CreateEditKeyLockerModal isOpen={Boolean(editKeyLockerPayload)} keyLocker={editKeyLockerPayload} onClose={() => setEditKeyLockerPayload(null)} />
      <KeyLockerBoxesModal
        keyLockerId={keyLockerBoxDetailsId}
        onClose={() => setKeyLockerBoxDetailsId(0)}
        keyLockerName={keyLockers?.find(keyLocker => keyLocker.id === keyLockerBoxDetailsId)?.name}
      />
    </>
  );
};
